<template>
  <div>
    <b-card no-body>
      <b-card-header header-tag="nav">
        <div class="d-flex align-items-center">
          <div class="mr-3">题材表格</div>
          <b-button size="sm" variant="outline-primary" class="mr-2" @click="loadTables(currentPage)" :disabled="loading">刷新</b-button>
          <b-badge variant="secondary">{{ tables.length }} 条</b-badge>
        </div>
      </b-card-header>
      <b-card-body>
        <div class="row">
          <div class="col-12 col-lg-8 mb-3">
            <b-table small striped hover responsive :items="tables" :fields="tableFields" :busy="loading" @row-clicked="onRowClicked">
              <template #cell(content)="{ value }">
                <div class="text-truncate" style="max-width: 200px;" :title="value">{{ value }}</div>
              </template>
              <template #cell(actions)="{ item }">
                <b-button size="sm" variant="link" @click.stop="selectTable(item)">查看</b-button>
                <b-button v-if="canEdit" size="sm" variant="link" @click.stop="editTable(item)">编辑</b-button>
              </template>
            </b-table>
            <div class="mt-2 d-flex justify-content-end" v-if="totalRows > perPage">
              <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="right" @change="loadTables"></b-pagination>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <b-card title="识别与创建" v-if="canEdit">
              <b-form-group label="名称">
                <b-form-input v-model="form.name" placeholder="请输入名称"></b-form-input>
              </b-form-group>
              <b-form-group label="内容">
                <b-form-textarea v-model="form.content" rows="3" max-rows="6" placeholder="请输入内容"></b-form-textarea>
              </b-form-group>
              <b-form-group label="上传图片">
                <b-form-file v-model="form.image" accept="image/*" @input="onImageChange"></b-form-file>
              </b-form-group>
              <div v-if="imagePreview" class="mb-2">
                <img :src="imagePreview" alt="预览" style="max-width:100%" />
              </div>
              <div class="d-flex">
                <b-button size="sm" variant="primary" class="mr-2" :disabled="submitting || !form.image" @click="recognizeImage">识别图片</b-button>
                <b-button size="sm" variant="success" :disabled="submitting || !form.name" @click="createTable">创建表格</b-button>
              </div>
              <b-alert :show="ocrMsg!==''" variant="info" class="mt-2">{{ ocrMsg }}</b-alert>
            </b-card>
            <b-alert v-else show variant="light" class="small text-muted">
              当前账号无创建/编辑权限（需要角色：post）。如需开通，请联系管理员。
            </b-alert>
          </div>
        </div>

        <div v-if="recognized.length" class="mt-3">
          <b-card title="识别结果">
            <b-table small striped hover responsive :items="recognized" :fields="recognizedFields">
              <template #cell(一级大类)="{ item }"><b-form-input v-model="item['一级大类']" size="sm"></b-form-input></template>
              <template #cell(二级小类)="{ item }"><b-form-input v-model="item['二级小类']" size="sm"></b-form-input></template>
              <template #cell(三级分类)="{ item }"><b-form-input v-model="item['三级分类']" size="sm"></b-form-input></template>
              <template #cell(个股)="{ item }"><b-form-input v-model="item['个股']" size="sm"></b-form-input></template>
              <template #cell(涨跌幅)="{ item }">
                <b-form-input v-if="isEditing" v-model="item['涨跌幅']" size="sm"></b-form-input>
                <span v-else :style="getRateStyle(item['涨跌幅'])">{{ item['涨跌幅'] }}<span v-if="item['涨跌幅']">%</span></span>
              </template>
              <template #cell(相关性)="{ item }"><b-form-input v-model="item['相关性']" size="sm"></b-form-input></template>
              <template #cell(信息源)="{ item }"><b-form-input v-model="item['信息源']" size="sm"></b-form-input></template>
              <template #cell(actions)="{ index }">
                <b-button size="sm" variant="danger" @click="recognized.splice(index, 1)">删除</b-button>
              </template>
            </b-table>
          </b-card>
        </div>

        <b-modal :title="isEditing ? '编辑题材表格' : '查看题材表格'" v-model="showEdit" dialog-class="wonder-topic-modal" hide-footer>
          <div>
            <div class="d-flex justify-content-between align-items-center mb-3">
               <div>
                  <div class="mb-2"><strong>名称：</strong>{{ editForm.name }}</div>
                  <div class="mb-2" style="white-space: pre-wrap;"><strong>内容：</strong>{{ editForm.content }}</div>
               </div>
               <div v-if="!isEditing">
                  <div class="d-flex align-items-center">
                    <b-dropdown v-if="viewMode==='mindmap'" size="sm" variant="outline-info" text="外观" class="mr-2">
                      <b-dropdown-header>线条风格</b-dropdown-header>
                      <b-dropdown-item @click="changeMindMapCurve('curve')" :active="mindMapCurve==='curve'">曲线</b-dropdown-item>
                      <b-dropdown-item @click="changeMindMapCurve('polyline')" :active="mindMapCurve==='polyline'">折线</b-dropdown-item>
                      <b-dropdown-divider></b-dropdown-divider>
                      <b-dropdown-header>节点形状</b-dropdown-header>
                      <b-dropdown-item @click="changeMindMapSymbol('emptyCircle')" :active="mindMapSymbol==='emptyCircle'">空心圆</b-dropdown-item>
                      <b-dropdown-item @click="changeMindMapSymbol('circle')" :active="mindMapSymbol==='circle'">实心圆</b-dropdown-item>
                      <b-dropdown-item @click="changeMindMapSymbol('roundRect')" :active="mindMapSymbol==='roundRect'">圆角矩形</b-dropdown-item>
                      <b-dropdown-item @click="changeMindMapSymbol('rect')" :active="mindMapSymbol==='rect'">矩形</b-dropdown-item>
                    </b-dropdown>
                    <b-button size="sm" variant="outline-success" class="mr-2" @click="queryStockReturns" :disabled="loadingStocks">
                       <b-spinner small v-if="loadingStocks"></b-spinner>
                       {{ loadingStocks ? '查询中' : '查询行情' }}
                    </b-button>
                    <b-button-group size="sm">
                      <b-button :variant="viewMode==='table'?'primary':'outline-secondary'" @click="viewMode='table'">表格</b-button>
                      <b-button :variant="viewMode==='mindmap'?'primary':'outline-secondary'" @click="viewMode='mindmap'">思维导图</b-button>
                    </b-button-group>
                  </div>
               </div>
            </div>

            <div v-if="viewMode==='mindmap'" style="height: 900px; border: 1px solid #eee;">
               <v-chart :key="mindMapStyle + mindMapCurve + mindMapSymbol" :options="mindMapOption" autoresize style="width:100%; height:100%;" />
            </div>

            <b-table v-if="viewMode==='table'" small striped hover responsive :items="editForm.rows" :fields="recognizedFields">
              <template #cell(一级大类)="{ item }">
                <b-form-input v-if="isEditing" v-model="item['一级大类']" size="sm"></b-form-input>
                <span v-else>{{ item['一级大类'] }}</span>
              </template>
              <template #cell(二级小类)="{ item }">
                <b-form-input v-if="isEditing" v-model="item['二级小类']" size="sm"></b-form-input>
                <span v-else>{{ item['二级小类'] }}</span>
              </template>
              <template #cell(三级分类)="{ item }">
                <b-form-input v-if="isEditing" v-model="item['三级分类']" size="sm"></b-form-input>
                <span v-else>{{ item['三级分类'] }}</span>
              </template>
              <template #cell(个股)="{ item }">
                <b-form-input v-if="isEditing" v-model="item['个股']" size="sm"></b-form-input>
                <span v-else>{{ item['个股'] }}</span>
              </template>
              <template #cell(涨跌幅)="{ item }">
                <b-form-input v-if="isEditing" v-model="item['涨跌幅']" size="sm"></b-form-input>
                <span v-else :style="getRateStyle(item['涨跌幅'])">{{ item['涨跌幅'] }}<span v-if="item['涨跌幅']">%</span></span>
              </template>
              <template #cell(相关性)="{ item }">
                <b-form-input v-if="isEditing" v-model="item['相关性']" size="sm"></b-form-input>
                <span v-else>{{ item['相关性'] }}</span>
              </template>
              <template #cell(信息源)="{ item }">
                <b-form-input v-if="isEditing" v-model="item['信息源']" size="sm"></b-form-input>
                <span v-else>{{ item['信息源'] }}</span>
              </template>
              <template #cell(actions)="{ index }">
                <b-button v-if="isEditing" size="sm" variant="danger" class="mr-1" @click="editForm.rows.splice(index, 1)" title="删除">
                  <b-icon icon="trash"></b-icon>
                </b-button>
                <b-button v-if="isEditing" size="sm" variant="success" @click="addRow(index)" title="新增">
                  <b-icon icon="plus"></b-icon>
                </b-button>
              </template>
            </b-table>
            <div v-if="isEditing" class="mt-3">
              <b-form-group label="名称">
                <b-form-input v-model="editForm.name"></b-form-input>
              </b-form-group>
              <b-form-group label="内容">
                <b-form-textarea v-model="editForm.content" rows="3"></b-form-textarea>
              </b-form-group>
              <b-button size="sm" variant="primary" :disabled="submitting" @click="updateTable">保存修改</b-button>
              <div class="mt-3 border-top pt-3">
                <b-form-group label="重新识别覆盖内容">
                  <b-form-file v-model="editForm.image" accept="image/*" @input="onEditImageChange"></b-form-file>
                </b-form-group>
                <div v-if="editImagePreview" class="mb-2">
                  <img :src="editImagePreview" alt="预览" style="max-width:100%" />
                </div>
                <div class="d-flex">
                  <b-button size="sm" variant="info" class="mr-2" :disabled="submitting || !editForm.image" @click="recognizeEditImage('overwrite')">识别并覆盖</b-button>
                  <b-button size="sm" variant="success" :disabled="submitting || !editForm.image" @click="recognizeEditImage('append')">识别并追加</b-button>
                </div>
                <b-alert :show="editOcrMsg!==''" variant="info" class="mt-2">{{ editOcrMsg }}</b-alert>
              </div>
            </div>
            <div v-if="isEditing && !canEdit" class="text-muted small mt-3">提示：没有编辑权限（需要 post 角色）。</div>
          </div>
        </b-modal>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/tree'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/graphic'
import 'echarts/lib/component/title'

export default {
  components: { 'v-chart': ECharts },
  data(){
    return {
      tables: [],
      tableFields: [ { key:'created_time', label:'创建时间', sortable:true }, { key:'updated_time', label:'更改时间', sortable:true }, { key:'name', label:'名称', sortable:true }, { key:'content', label:'内容' }, { key:'actions', label:'操作' } ],
      loading: false,
      submitting: false,
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      form: { name:'', content:'', image:null },
      imagePreview: '',
      recognized: [],
      ocrMsg: '',
      recognizedFields: [ { key:'一级大类', label:'一级大类' }, { key:'二级小类', label:'二级小类' }, { key:'三级分类', label:'三级分类' }, { key:'个股', label:'个股' }, { key:'相关性', label:'相关性' }, { key:'信息源', label:'信息源' }, { key:'actions', label:'操作' } ],
      showEdit: false,
      editId: null,
      editForm: { name:'', content:'', rows:[], image: null },
      editImagePreview: '',
      editOcrMsg: '',
      isEditing: false,
      viewMode: 'table',
      mindMapOption: {},
      mindMapStyle: 'LR',
      mindMapCurve: 'curve',
      mindMapSymbol: 'emptyCircle',
      loadingStocks: false
    }
  },
  computed:{
    canEdit(){
      try{
        const raw = localStorage.getItem('user_info')
        if(!raw) return false
        const u = JSON.parse(raw)
        const r = u && u.roles
        const arr = Array.isArray(r) ? r : String(r||'').split(',').map(s=>s.trim()).filter(Boolean)
        return arr.includes('post')
      }catch(e){ return false }
    }
  },
  mounted(){ 
    this.loadTables()
    window.addEventListener('paste', this.handlePaste)
  },
  beforeDestroy() {
    window.removeEventListener('paste', this.handlePaste)
  },
  methods:{
    handlePaste(event){
      const items = (event.clipboardData || event.originalEvent.clipboardData).items
      for (let index in items) {
        const item = items[index]
        if (item.kind === 'file' && item.type.indexOf('image/') !== -1) {
          const blob = item.getAsFile()
          if (this.showEdit) {
             this.editForm.image = blob
             this.onEditImageChange(blob)
             // Auto recognize if desired, or just show preview
          } else {
             this.form.image = blob
             this.onImageChange(blob)
          }
          event.preventDefault()
          return
        }
      }
    },
    onImageChange(file){ if (file) { try { this.imagePreview = URL.createObjectURL(file) } catch(e){ this.imagePreview='' } } else { this.imagePreview='' } },
    onEditImageChange(file){ if (file) { try { this.editImagePreview = URL.createObjectURL(file) } catch(e){ this.editImagePreview='' } } else { this.editImagePreview='' } },
    loadTables(page=1){
      this.loading = true
      this.currentPage = page
      request_zzquant({ url:'/topic/tables', method:'GET', params:{ page: this.currentPage, limit: this.perPage } })
        .then(res=>{
          const d = res && res.data && res.data.data ? res.data.data : {}
          if(Array.isArray(d)){
             // Compatible with old format if backend not updated immediately
             this.tables = d
             this.totalRows = d.length
          } else {
             this.tables = Array.isArray(d.items) ? d.items : []
             this.totalRows = d.total || 0
          }
        })
        .catch(()=>{ this.tables = [] })
        .finally(()=>{ this.loading = false })
    },
    recognizeImage(){
      if(!this.form.image){ this.ocrMsg = '请先选择图片'; return }
      this.submitting = true
      this.ocrMsg = '正在识别…'
      const fd = new FormData()
      fd.append('image', this.form.image)
      fd.append('engine', 'qwen')
      request_zzquant({ url:'/topic/table/ocr', method:'POST', data: fd, headers:{ 'Content-Type':'multipart/form-data' }, timeout: 60000 })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000){
            const rows = d.data || []
            this.recognized = Array.isArray(rows) ? rows : []
            this.ocrMsg = this.recognized.length ? '识别成功' : '未识别到表格内容'
          } else {
            const msg = d.data || d.message || '识别失败'
            this.recognized = []
            this.ocrMsg = typeof msg === 'string' ? msg : '识别失败'
          }
        })
        .catch(err=>{ const msg = (err && err.message) ? String(err.message) : '识别失败'; this.recognized = []; this.ocrMsg = msg })
        .finally(()=>{ this.submitting = false })
    },
    recognizeEditImage(mode = 'overwrite'){
      if(!this.editForm.image){ this.editOcrMsg = '请先选择图片'; return }
      this.submitting = true
      this.editOcrMsg = '正在识别…'
      const fd = new FormData()
      fd.append('image', this.editForm.image)
      fd.append('engine', 'qwen')
      request_zzquant({ url:'/topic/table/ocr', method:'POST', data: fd, headers:{ 'Content-Type':'multipart/form-data' }, timeout: 60000 })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000){
            const rows = d.data || []
            const newRows = Array.isArray(rows) ? rows : []
            if (mode === 'append') {
              this.editForm.rows = [...(this.editForm.rows || []), ...newRows]
              this.editOcrMsg = newRows.length ? '识别成功并已追加内容' : '未识别到表格内容'
            } else {
              this.editForm.rows = newRows
              this.editOcrMsg = newRows.length ? '识别成功并已覆盖内容' : '未识别到表格内容'
            }
          } else {
            const msg = d.data || d.message || '识别失败'
            this.editOcrMsg = typeof msg === 'string' ? msg : '识别失败'
          }
        })
        .catch(err=>{ const msg = (err && err.message) ? String(err.message) : '识别失败'; this.editOcrMsg = msg })
        .finally(()=>{ this.submitting = false })
    },
    createTable(){
      if(!this.canEdit) return
      const name = (this.form.name||'').trim()
      const content = (this.form.content||'').trim()
      if(!name) return
      this.submitting = true
      const fd = new FormData()
      fd.append('name', name)
      fd.append('content', content)
      if(this.form.image) fd.append('image', this.form.image)
      if(this.recognized && this.recognized.length) fd.append('rows', JSON.stringify(this.recognized))
      request_zzquant({ url:'/topic/table/create', method:'POST', data: fd })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000){
            this.form={ name:'', content:'', image:null }
            this.imagePreview=''
            this.recognized=[]
            this.loadTables()
            // this.$bvToast.toast('创建成功', { variant:'success', solid:true })
          } else {
            const msg = d.data || d.message || '创建失败'
            alert(typeof msg === 'string' ? msg : '创建失败')
          }
        })
        .catch(err=>{ console.error(err); alert('请求失败') })
        .finally(()=>{ this.submitting=false })
    },
    selectTable(item){ 
      this.editId = item && item.id ? item.id : null; 
      this.editForm = { 
        name: item.name || '', 
        content: item.content || '', 
        rows: item.rows || [], 
        image: null,
        updated_time: item.updated_time || ''
      }; 
      this.editImagePreview = ''; 
      this.editOcrMsg = ''; 
      this.isEditing = false;
      this.viewMode = 'mindmap';
      this.generateMindMap();
      this.showEdit = true; 
    },
    editTable(item){ 
      this.selectTable(item); 
      this.isEditing = true; 
      this.viewMode = 'table';
    },
    generateMindMap(){
      const data = { name: this.editForm.name || '题材', children: [] };
      const map = {};
      // Layout config
      const layoutMap = {
          'LR': { orient: 'LR', top: '5%', left: '5%', bottom: '5%', right: '70%', labelPos: 'left', leafLabelPos: 'right', labelAlign: 'right', leafLabelAlign: 'left' },
          'RL': { orient: 'RL', top: '5%', left: '70%', bottom: '5%', right: '5%', labelPos: 'right', leafLabelPos: 'left', labelAlign: 'left', leafLabelAlign: 'right' },
          'TB': { orient: 'TB', top: '5%', left: '5%', bottom: '70%', right: '5%', labelPos: 'top', leafLabelPos: 'bottom', labelAlign: 'center', leafLabelAlign: 'center', labelVertical: 'bottom', leafLabelVertical: 'top' },
          'BT': { orient: 'BT', top: '70%', left: '5%', bottom: '5%', right: '5%', labelPos: 'bottom', leafLabelPos: 'top', labelAlign: 'center', leafLabelAlign: 'center', labelVertical: 'top', leafLabelVertical: 'bottom' }
      };
      const currentStyle = layoutMap[this.mindMapStyle] || layoutMap['LR'];
      const stockLabel = {
          position: currentStyle.leafLabelPos,
          verticalAlign: currentStyle.leafLabelVertical || 'middle',
          align: currentStyle.leafLabelAlign,
          fontSize: 12,
          rotate: (this.mindMapStyle === 'TB' || this.mindMapStyle === 'BT') ? 90 : 0
      };

      (this.editForm.rows || []).forEach(row => {
          const l1 = (row['一级大类'] || '其他').trim();
          const l2 = (row['二级小类'] || '').trim();
          const l3 = (row['三级分类'] || '').trim();
          const stock = (row['个股'] || '').trim();
          if (!stock) return;

          let stockName = stock;
          let nodeColor = undefined;
          let nodeFontWeight = undefined;

          if (row['涨跌幅'] !== undefined && row['涨跌幅'] !== null) {
             stockName += ` (${row['涨跌幅']}%)`;
             const rate = parseFloat(row['涨跌幅']);
             if (!isNaN(rate)) {
                if (rate > 9) {
                    nodeColor = 'red';
                    nodeFontWeight = 'bold';
                } else if (rate > 0) {
                    nodeColor = 'red';
                } else if (rate < 0) {
                    nodeColor = 'green';
                }
             }
          }

          if (!map[l1]) map[l1] = {};
          if (!map[l1][l2]) map[l1][l2] = {};
          if (!map[l1][l2][l3]) map[l1][l2][l3] = [];

          map[l1][l2][l3].push({ 
             name: stockName, 
             value: row['相关性'] || '', 
             source: row['信息源'] || '',
             color: nodeColor,
             fontWeight: nodeFontWeight
          });
      });

      // Colors for different branches
      const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
      let colorIndex = 0;

      for (const l1 in map) {
          const color = colors[colorIndex % colors.length];
          colorIndex++;
          const l1Node = { 
            name: l1, 
            children: [],
            itemStyle: { color: color, borderColor: color },
            lineStyle: { color: color }
          };
          for (const l2 in map[l1]) {
                  let l2Parent = l1Node;
                  if (l2) {
                    const l2Node = { 
                      name: l2, 
                      children: [],
                      itemStyle: { color: color, borderColor: color },
                      lineStyle: { color: color }
                    };
                    l1Node.children.push(l2Node);
                    l2Parent = l2Node;
                  }

                  for (const l3 in map[l1][l2]) {
                      let l3Parent = l2Parent;
                      if (l3) {
                          const l3Node = { 
                              name: l3, 
                              children: [],
                              itemStyle: { color: color, borderColor: color },
                              lineStyle: { color: color }
                          };
                          l2Parent.children.push(l3Node);
                          l3Parent = l3Node;
                      }

                      map[l1][l2][l3].forEach(s => {
                          const stockNode = {
                              name: s.name,
                              children: [],
                              itemStyle: { color: color, borderColor: color },
                              lineStyle: { color: color },
                              label: {
                                  ...stockLabel,
                                  color: s.color,
                                  fontWeight: s.fontWeight
                              }
                          };
                          // Helper to truncate text
                          const truncate = (str, len=200) => str.length > len ? str.substring(0, len) + '...' : str;
                          
                          let lastNode = stockNode;
        
                          if(s.source) {
                            const sourceNode = { 
                              name: truncate(s.source) + '：' + truncate(s.value),
                              value: s.source, // Store full text for tooltip
                              itemStyle: { color: color, borderColor: color },
                              lineStyle: { color: color },
                              children: []
                            };
                            lastNode.children.push(sourceNode);
                            lastNode = sourceNode;
                          }
                          
                          l3Parent.children.push(stockNode);
                      });
                  }
              }
          data.children.push(l1Node);
      }

      const getLayoutOption = (style) => {
          const base = {
              type: 'tree',
              data: [data],
              symbol: this.mindMapSymbol,
              edgeShape: this.mindMapCurve,
              symbolSize: 7,
              roam: true,
              expandAndCollapse: true,
              initialTreeDepth: -1,
              animationDuration: 550,
              animationDurationUpdate: 750
          };
          
          if (style === 'radial') {
              return {
                  ...base,
                  layout: 'radial',
                  top: '10%', left: '10%', bottom: '10%', right: '10%',
                  label: { position: 'right', verticalAlign: 'middle', align: 'left', fontSize: 12 },
                  leaves: { label: { position: 'right', verticalAlign: 'middle', align: 'left' } }
              };
          }
          
          const opt = layoutMap[style] || layoutMap['LR'];
          return {
              ...base,
              layout: 'orthogonal',
              orient: opt.orient,
              top: opt.top, left: opt.left, bottom: opt.bottom, right: opt.right,
              label: { 
                  position: opt.labelPos, 
                  verticalAlign: opt.labelVertical || 'middle', 
                  align: opt.labelAlign, 
                  fontSize: 12,
                  rotate: (style === 'TB' || style === 'BT') ? 90 : 0
              },
              leaves: { 
                  label: { 
                      position: opt.leafLabelPos, 
                      verticalAlign: opt.leafLabelVertical || 'middle', 
                      align: opt.leafLabelAlign,
                      rotate: (style === 'TB' || style === 'BT') ? 90 : 0
                  } 
              }
          };
      };

      this.mindMapOption = {
          title: {
            text: this.editForm.updated_time ? '数据更新时间：' + this.editForm.updated_time : '',
            top: 10,
            left: 10,
            textStyle: { fontSize: 12, color: '#999', fontWeight: 'normal' }
          },
          tooltip: { 
            trigger: 'item', 
            triggerOn: 'mousemove',
            formatter: (params) => {
              // Show full text if available in value, otherwise name
              return params.data.value ? params.data.value : params.name;
            }
          },
          series: [getLayoutOption(this.mindMapStyle)],
          graphic: [
            {
              type: 'text',
              z: -10,
              right: 20,
              bottom: 60,
              style: {
                fill: '#e0e0e0',
                text: '自在量化',
                font: 'bold 50px sans-serif'
              }
            },
            {
              type: 'text',
              z: -10,
              right: 20,
              bottom: 20,
              style: {
                fill: '#e0e0e0',
                text: 'quant.zizizaizai.com',
                font: '24px sans-serif'
              }
            }
          ]
      };
    },
    changeMindMapStyle(style){
      this.mindMapStyle = style;
      this.generateMindMap();
    },
    changeMindMapCurve(curve){
      this.mindMapCurve = curve;
      this.generateMindMap();
    },
    changeMindMapSymbol(symbol){
      this.mindMapSymbol = symbol;
      this.generateMindMap();
    },
    queryStockReturns(){
      if(!this.editForm.rows || !this.editForm.rows.length) return
      
      const stocks = this.editForm.rows.map(r => r['个股']).filter(s => s && s.trim())
      if(!stocks.length){
        alert('没有找到个股代码')
        return
      }
      
      this.loadingStocks = true
      
      request_zzquant({ 
        url:'/topic/table/stock-returns', 
        method:'POST', 
        data: { stocks } 
      })
      .then(res => {
         const d = res.data
         if(d.code === 20000){
            const result = d.data || {}
            // Update rows with new data
            let updatedCount = 0
            this.editForm.rows = this.editForm.rows.map(row => {
               const stock = (row['个股'] || '').trim()
               if(stock && result[stock]){
                  updatedCount++
                  return {
                     ...row,
                     '涨跌幅': result[stock].px_change_rate,
                     '最新价': result[stock].last_px
                  }
               }
               return row
            })
            
            // Check if fields exist in recognizedFields
            const hasQuote = this.recognizedFields.some(f => f.key === '涨跌幅')
            if(!hasQuote){
               // Add new columns before '相关性' (index 4)
               // current: [一级大类, 二级小类, 三级分类, 个股, 相关性, 信息源, actions]
               this.recognizedFields.splice(4, 0, { key:'最新价', label:'最新价' }, { key:'涨跌幅', label:'涨跌幅' })
            }
            
            // Refresh mind map to show price changes
            if(this.viewMode === 'mindmap'){
               this.generateMindMap()
            }

            this.$bvToast.toast(`已更新 ${updatedCount} 个股票行情`, { variant:'success', solid:true })
         } else {
            alert(d.message || '查询失败')
         }
      })
      .catch(e => {
         console.error(e)
         alert('查询出错')
      })
      .finally(() => {
         this.loadingStocks = false
      })
    },
    updateTable(){
      if(!this.canEdit || !this.editId) return
      this.submitting = true
      const payload = { name: this.editForm.name, content: this.editForm.content, rows: this.editForm.rows }
      request_zzquant({ url:'/topic/table/update/'+this.editId, method:'POST', data: payload })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000){
            this.showEdit=false
            this.loadTables()
          } else {
            const msg = d.data || d.message || '更新失败'
            alert(typeof msg === 'string' ? msg : '更新失败')
          }
        })
        .catch(err=>{ console.error(err); alert('请求失败') })
        .finally(()=>{ this.submitting=false })
    },
    addRow(index) {
      const newRow = {
        '一级大类': '',
        '二级小类': '',
        '三级分类': '',
        '个股': '',
        '相关性': '',
        '信息源': ''
      };
      this.editForm.rows.splice(index + 1, 0, newRow);
    },
    getRateStyle(rate) {
        if (rate === undefined || rate === null) return {};
        const r = parseFloat(rate);
        if (isNaN(r)) return {};
        if (r > 9) return { color: 'red', fontWeight: 'bold' };
        if (r > 5) return { color: 'red', fontWeight: 'bold' };
        if (r > 0) return { color: 'red' };
        if (r < 0) return { color: 'green' };
        return {};
    },
    onRowClicked(item){ this.selectTable(item) }
  }
}
</script>

<style>
@media (max-width: 991px) {
  .wonder-topic-modal {
    max-width: 100% !important;
    margin: 0 !important;
  }
  .wonder-topic-modal .modal-content {
    min-height: 100vh !important;
    border-radius: 0 !important;
  }
}
@media (min-width: 992px) {
  .wonder-topic-modal {
    max-width: 80% !important;
    margin: 30px auto !important; 
  }
}
</style>
